/**
 * The Integrations section of an app card: one line per connection, in the
 * order the Manage panel shows the full cards, and a Manage button that
 * opens that panel.
 *
 * Each line's badge repeats the badge on its card in the panel, worked out
 * from the same data the same way, so the two never disagree. Nothing on
 * the page showed a connection's status in one line before: each badge sat
 * inside its card, behind the old Integrations tab.
 */
import type { ReactNode } from 'react'
import { Loader2, MessageSquare, Workflow } from 'lucide-react'
import { Badge, Button } from '@/components/ui'
import { GitHubIcon } from '@/auth/GitHubIcon'
import { TrackerLogo } from '@/components/TrackerLogo'
import { useTrackerDescriptors, descriptorFor, connectionNameOf } from '@/hooks/useTrackerDescriptors'
import type { AppIntegrationsResponse } from '@/lib/api'
import { useAppIntegrations } from './useAppIntegrations'

export function AppIntegrationsSummary() {
  const { appId, data, loading, loadError, openPanel } = useAppIntegrations()
  // One id per app: with no app selected, several cards share the page.
  const headingId = `app-integrations-heading-${appId}`

  return (
    <section aria-labelledby={headingId}>
      <div className="flex items-center justify-between mb-1.5">
        <h3 id={headingId} className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Integrations
        </h3>
        <Button variant="ghost" size="sm" onClick={openPanel} disabled={!data}>
          Manage
        </Button>
      </div>
      {loading && !data ? (
        <p className="flex items-center text-xs text-muted-foreground py-1">
          <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
          Loading integrations…
        </p>
      ) : !data ? (
        <p className="text-xs text-destructive py-1">Couldn't load integrations: {loadError ?? 'unknown error'}</p>
      ) : (
        <ul className="divide-y divide-border">
          <IssueTrackerLine data={data} />
          <SummaryLine
            icon={<span className="flex h-5 w-5 items-center justify-center rounded bg-[#1f2328] text-white"><GitHubIcon className="h-3 w-3" /></span>}
            name="GitHub repository"
            {...githubLine(data)}
          />
          <SummaryLine
            icon={<span className="flex h-5 w-5 items-center justify-center rounded bg-gradient-to-br from-primary to-purple-600 text-white"><Workflow className="h-3 w-3" /></span>}
            name="Pipeline Integration"
            value="Run OttoTester from your own CI"
            badge={<Badge variant="outline">Available</Badge>}
          />
          <SummaryLine
            icon={<span className="flex h-5 w-5 items-center justify-center rounded bg-[#4a154b] text-white"><MessageSquare className="h-3 w-3" /></span>}
            name="Slack channel"
            {...slackLine(data)}
          />
        </ul>
      )}
    </section>
  )
}

interface LineParts {
  value: ReactNode
  badge?: ReactNode
}

function SummaryLine({ icon, name, value, badge }: { icon: ReactNode; name: string } & LineParts) {
  return (
    <li className="flex items-center gap-2 py-1.5 text-xs">
      {icon}
      <span className="w-36 shrink-0 font-medium">{name}</span>
      <span className="flex-1 min-w-0 truncate text-muted-foreground">{value}</span>
      {badge}
    </li>
  )
}

/** The GitHub line, matching the GitHub card's badge. Exported for unit
 * tests. */
export function githubLine(data: AppIntegrationsResponse): LineParts {
  if (!data.github.connected) {
    return { value: 'Not connected on this workspace', badge: <Badge variant="outline">GitHub not connected</Badge> }
  }
  const repo = data.bindings.github?.repoIdentifier
  return repo
    ? { value: repo, badge: <Badge variant="default">Linked</Badge> }
    : { value: 'No repository picked', badge: <Badge variant="outline">Not linked</Badge> }
}

/** The Slack line, matching the Slack card's badge. A saved channel the
 * channel list doesn't name (the list failed to load, or the channel is
 * gone) still reads as linked, as the card's badge does. Exported for unit
 * tests. */
export function slackLine(data: AppIntegrationsResponse): LineParts {
  if (!data.slack.connected) {
    return { value: 'Not connected on this workspace', badge: <Badge variant="outline">Slack not connected</Badge> }
  }
  const id = data.bindings.slack?.defaultChannelId
  if (!id) return { value: 'No channel picked', badge: <Badge variant="outline">Not linked</Badge> }
  const channel = data.slack.channels.find((c) => c.id === id)
  const label = channel ? (channel.isPrivate ? `🔒 ${channel.name}` : `# ${channel.name}`) : 'A channel is picked'
  return { value: label, badge: <Badge variant="default">Linked</Badge> }
}

function IssueTrackerLine({ data }: { data: AppIntegrationsResponse }) {
  const descriptors = useTrackerDescriptors()
  const binding = data.bindings.issueSource
  const connection = binding ? data.issueTracker.connections.find((c) => c.id === binding.connectionId) : undefined
  const descriptor = connection ? descriptorFor(descriptors, connection.provider) : undefined
  return (
    <SummaryLine
      icon={<TrackerLogo logoKey={descriptor?.logoKey} name={descriptor?.displayName ?? 'Issue tracker'} className="h-5 w-5 text-[10px]" />}
      name="Issue tracker"
      {...issueTrackerLine(data, descriptor ? connectionNameOf(descriptor) : undefined)}
    />
  )
}

/** The Issue tracker line. When the workspace's tracker list failed to
 * load, the line shows the server's error, as the card does. With a saved
 * link it keeps the link and its Linked badge beside the error. With none,
 * it shows the error and no badge: an empty list then does not mean nothing
 * is connected, so "Not linked" or "not connected" would send the reader to
 * connect a tracker the workspace may already have. Exported for unit
 * tests. */
export function issueTrackerLine(data: AppIntegrationsResponse, trackerName?: string): LineParts {
  const binding = data.bindings.issueSource
  const error = data.issueTracker.error
  const errorText = error ? <span className="text-amber-700 dark:text-amber-300">{error}</span> : null
  if (binding) {
    const projects = binding.projectKeys.map((k) => binding.projectNames?.[k] ?? k).join(', ')
    const linked = [trackerName, projects].filter(Boolean).join(' · ')
    return {
      value: errorText ? <>{linked} · {errorText}</> : linked,
      badge: <Badge variant="default">Linked</Badge>,
    }
  }
  if (errorText) return { value: errorText }
  if (data.issueTracker.connections.length === 0) {
    return { value: 'Not connected on this workspace', badge: <Badge variant="outline">Not linked</Badge> }
  }
  return { value: 'Not set up for this app', badge: <Badge variant="outline">Not linked</Badge> }
}
